import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle, 
  Truck, 
  MapPin, 
  Phone, 
  User, 
  Calendar,
  DollarSign,
  Package,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { formatPrice } from '../../lib/utils';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc,
  query,
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const OrdersManagementNew = () => {
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      console.log('🔄 Updating order status:', { orderId, newStatus });
      
      // Validate inputs
      if (!orderId || !newStatus) {
        throw new Error('Missing orderId or newStatus');
      }
      
      // Update the order in Firestore
      const orderRef = doc(db, 'orders', orderId);
      
      // Try multiple approaches for updating order status
      let updateSuccess = false;
      
      // Method 1: Try with admin tracking
      try {
        await updateDoc(orderRef, {
          status: newStatus,
          updatedAt: new Date(),
          updatedBy: 'admin'
        });
        updateSuccess = true;
        console.log('✅ Updated with admin tracking');
      } catch (error1) {
        console.log('🔄 Method 1 failed, trying method 2...');
        
        // Method 2: Try without admin tracking
        try {
          await updateDoc(orderRef, {
            status: newStatus,
            updatedAt: new Date()
          });
          updateSuccess = true;
          console.log('✅ Updated without admin tracking');
        } catch (error2) {
          console.log('🔄 Method 2 failed, trying method 3...');
          
          // Method 3: Try with minimal data
          try {
            await updateDoc(orderRef, {
              status: newStatus
            });
            updateSuccess = true;
            console.log('✅ Updated with minimal data');
          } catch (error3) {
            console.log('❌ All update methods failed');
            throw error3; // Throw the last error
          }
        }
      }
      
      if (!updateSuccess) {
        throw new Error('All update methods failed');
      }
      
      console.log('✅ Order status updated successfully');
      
      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus, updatedAt: new Date() } : order
        )
      );
      
      toast.success(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        orderId,
        newStatus
      });
      
      // Provide specific error messages
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Check Firestore rules for orders collection.');
      } else if (error.code === 'not-found') {
        toast.error('Order not found. It may have been deleted.');
      } else if (error.code === 'unavailable') {
        toast.error('Service temporarily unavailable. Please try again.');
      } else {
        toast.error(`Failed to update order status: ${error.message}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': 
      case 'placed': return '#f59e0b';
      case 'accepted':
      case 'confirmed': return '#3b82f6';
      case 'preparing': return '#8b5cf6';
      case 'ready': return '#10b981';
      case 'out_for_delivery': return '#f97316';
      case 'delivered': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': 
      case 'placed': return <Clock style={{ width: '16px', height: '16px' }} />;
      case 'accepted': 
      case 'confirmed': return <CheckCircle style={{ width: '16px', height: '16px' }} />;
      case 'preparing': return <Package style={{ width: '16px', height: '16px' }} />;
      case 'ready': return <CheckCircle style={{ width: '16px', height: '16px' }} />;
      case 'out_for_delivery': return <Truck style={{ width: '16px', height: '16px' }} />;
      case 'delivered': return <CheckCircle style={{ width: '16px', height: '16px' }} />;
      case 'cancelled': return <XCircle style={{ width: '16px', height: '16px' }} />;
      default: return <Clock style={{ width: '16px', height: '16px' }} />;
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customerPhone.includes(searchQuery) ||
      order.deliveryAddress?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const OrderCard = ({ order }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onClick={() => {
      setSelectedOrder(order);
      setShowOrderDetails(true);
    }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '16px'
      }}>
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 4px 0'
          }}>
            Order #{order.orderId}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            {new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleString()}
          </p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: `${getStatusColor(order.status)}20`,
          color: getStatusColor(order.status),
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {getStatusIcon(order.status)}
          <span style={{ textTransform: 'capitalize' }}>
            {order.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <User style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
              {order.deliveryAddress?.name || 'N/A'}
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <Phone style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {order.customerPhone}
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <MapPin style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {order.deliveryAddress?.address?.substring(0, 30)}...
            </span>
          </div>
        </div>
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <Package style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {order.items?.length || 0} items
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <DollarSign style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              ₹{order.total || 0}
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock style={{ width: '16px', height: '16px', color: '#6b7280' }} />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              Est: {new Date(order.estimatedDelivery?.toDate?.() || order.estimatedDelivery).toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end'
      }}>
        {(order.status === 'placed' || order.status === 'pending') && (
          <>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                updateOrderStatus(order.id, 'accepted');
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Accept
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                updateOrderStatus(order.id, 'cancelled');
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </Button>
          </>
        )}
        {order.status === 'accepted' && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(order.id, 'preparing');
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
              Start Cooking
          </Button>
        )}
        {order.status === 'preparing' && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(order.id, 'ready');
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
              Ready for Pickup
          </Button>
        )}
        {order.status === 'ready' && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(order.id, 'out_for_delivery');
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
              Dispatch Order
          </Button>
        )}
        {order.status === 'out_for_delivery' && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              updateOrderStatus(order.id, 'delivered');
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
              Complete Delivery
          </Button>
        )}
      </div>
    </div>
  );

  const OrderDetailsModal = () => {
    if (!selectedOrder) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '32px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>
              Order Details
            </h2>
            <button
              onClick={() => setShowOrderDetails(false)}
              style={{
                padding: '8px',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <XCircle style={{ width: '20px', height: '20px' }} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                Order Information
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Order ID:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    #{selectedOrder.orderId}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Status:</span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: getStatusColor(selectedOrder.status),
                    textTransform: 'capitalize'
                  }}>
                    {selectedOrder.status.replace('_', ' ')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Order Time:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {new Date(selectedOrder.createdAt?.toDate?.() || selectedOrder.createdAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Est. Delivery:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {new Date(selectedOrder.estimatedDelivery?.toDate?.() || selectedOrder.estimatedDelivery).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                Customer Information
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Name:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedOrder.deliveryAddress?.name || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Phone:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedOrder.customerPhone}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Address:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedOrder.deliveryAddress?.address || 'N/A'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Pincode:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedOrder.deliveryAddress?.pincode || 'N/A'}
                  </span>
                </div>
                {selectedOrder.deliveryAddress?.coordinates && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Coordinates:</span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                        {selectedOrder.deliveryAddress.coordinates.lat?.toFixed(6)}, {selectedOrder.deliveryAddress.coordinates.lng?.toFixed(6)}
                      </span>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <a
                        href={`https://www.google.com/maps?q=${selectedOrder.deliveryAddress.coordinates.lat},${selectedOrder.deliveryAddress.coordinates.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          backgroundColor: '#ff6b35',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
                      >
                        <MapPin size={12} />
                        View on Google Maps
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Order Items
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedOrder.items?.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0' }}>
                      {item.name}
                    </h4>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                      {item.portion} portion
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      ₹{item.price}
                    </span>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                      Qty: {item.quantity}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Order Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Subtotal:</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                  ₹{selectedOrder.subtotal || 0}
                </span>
              </div>
              {selectedOrder.discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', color: '#059669' }}>Discount:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                    -₹{selectedOrder.discount || 0}
                  </span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '8px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Total:</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                  ₹{selectedOrder.total || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading orders...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Orders Management</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage customer orders</p>
              </div>
            </div>
            
            <Button
              onClick={fetchOrders}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Search and Filter */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                width: '20px',
                height: '20px'
              }} />
              <Input
                type="text"
                placeholder="Search orders by ID, phone, or customer name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '12px 12px 12px 40px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="ready">Ready</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
              {orders.length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Total Orders</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b', margin: '0 0 4px 0' }}>
              {orders.filter(order => order.status === 'pending' || order.status === 'placed').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Pending</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#059669', margin: '0 0 4px 0' }}>
              {orders.filter(order => order.status === 'delivered').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Delivered</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626', margin: '0 0 4px 0' }}>
              {orders.filter(order => order.status === 'cancelled').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Cancelled</p>
          </div>
        </div>

        {/* Orders Grid */}
        <div style={{
          display: 'grid',
          gap: '20px'
        }}>
          {filteredOrders.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px'
              }}>
                📦
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                No orders found
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter' 
                  : 'No orders have been placed yet'
                }
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))
          )}
        </div>
      </div>

      {showOrderDetails && <OrderDetailsModal />}
    </div>
  );
};

export default OrdersManagementNew;
